import React from 'react';
/**
 * LineChartRenderer — Interactive line chart with dot selection.
 *
 * DDD co-locatie: moved from components/practice/renderers/ in s67.
 * Optimized: extracted shared data normalization + selection hook (~272r → ~155r).
 */

import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { QuestionRendererProps } from './types';
import { normalizeChartData, ChartDataPoint } from './chartDataUtils';
import { useChartSelection } from './useChartSelection';
export function LineChartRenderer({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate
}: QuestionRendererProps) {
  const config = question.data || {};
  const data = normalizeChartData(config);
  const xAxisLabel = config.xAxisLabel || '';
  const yAxisLabel = config.yAxisLabel || '';
  const lineColor = config.lineColor || '#6366f1';
  const hideValues = config.hideValues !== false;
  const {
    selectedIndex,
    handleItemClick,
    handleKeyDown
  } = useChartSelection({
    questionId: question.id,
    itemCount: data.length,
    idPrefix: 'line-option',
    onAnswer,
    onReadyChange,
    shouldValidate
  });
  const CustomDot = (props: {
    cx?: number;
    cy?: number;
    index?: number;
  }) => {
    const {
      cx,
      cy,
      index
    } = props;
    if (cx === undefined || cy === undefined) return null;
    const isSelected = selectedIndex === index;
    return <g data-id="element-7299">
        <circle cx={cx} cy={cy} r={20} fill="transparent" cursor="pointer" onClick={() => handleItemClick(null, index!)} data-id="element-7300" />
        <circle cx={cx} cy={cy} r={isSelected ? 10 : 6} fill={isSelected ? '#4f46e5' : '#fff'} stroke={isSelected ? '#312e81' : lineColor} strokeWidth={isSelected ? 3 : 2} cursor="pointer" onClick={() => handleItemClick(null, index!)} data-id="element-7301" />
        {isSelected && <circle cx={cx} cy={cy} r={16} fill="none" stroke="#6366f1" strokeWidth={2} strokeDasharray="4 2" opacity={0.5} data-id="element-7302" />}
      </g>;
  };
  const CustomTooltip = ({
    active,
    payload
  }: {
    active?: boolean;
    payload?: Array<{
      payload: {
        label: string;
        value: number;
      };
    }>;
  }) => {
    if (!active || !payload?.[0]) return null;
    const item = payload[0].payload;
    return <div className="bg-white px-3 py-2 rounded-lg shadow-lg border border-slate-200 text-sm" data-id="element-7303">
        <span className="font-semibold text-slate-700" data-id="element-7304">{item.label}</span>
        {!hideValues && <span className="text-slate-500 ml-2" data-id="element-7305">{item.value}</span>}
      </div>;
  };
  return <div className="w-full max-w-3xl mx-auto flex flex-col items-center" data-id="element-7306">
      <div className="w-full h-[300px] md:h-[400px] bg-white rounded-xl border border-slate-200 shadow-sm p-4" aria-hidden="true" data-id="element-7307">
        <ResponsiveContainer width="100%" height="100%" data-id="element-7308">
          <LineChart data={data} margin={{
          top: 20,
          right: 30,
          left: 20,
          bottom: 40
        }} data-id="element-7309">
            <CartesianGrid strokeDasharray="3 3" vertical={false} data-id="element-7310" />
            <XAxis dataKey="label" tick={{
            fill: '#64748b',
            fontSize: 12
          }} tickLine={false} axisLine={{
            stroke: '#cbd5e1'
          }} label={xAxisLabel ? {
            value: xAxisLabel,
            position: 'insideBottom',
            offset: -20,
            fill: '#475569',
            fontSize: 14,
            fontWeight: 600
          } : undefined} data-id="element-7311" />
            <YAxis tick={{
            fill: '#64748b',
            fontSize: 12
          }} tickLine={false} axisLine={false} label={yAxisLabel ? {
            value: yAxisLabel,
            angle: -90,
            position: 'insideLeft',
            fill: '#475569',
            fontSize: 14,
            fontWeight: 600
          } : undefined} data-id="element-7312" />
            <Tooltip content={<CustomTooltip data-id="element-7314" />} cursor={{
            stroke: '#94a3b8',
            strokeWidth: 1,
            strokeDasharray: '4 4'
          }} data-id="element-7313" />
            <Line type="monotone" dataKey="value" stroke={lineColor} strokeWidth={3} dot={<CustomDot data-id="element-7316" />} activeDot={false} data-id="element-7315" />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div role="listbox" aria-label="Kies een punt" className="flex flex-wrap justify-center gap-2 mt-4" data-id="element-7317">
        {data.map((entry: ChartDataPoint, index: number) => <button key={`option-${index}`} id={`line-option-${index}`} role="option" aria-selected={selectedIndex === index} tabIndex={0} onClick={() => handleItemClick(null, index)} onKeyDown={e => handleKeyDown(e, index)} className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${selectedIndex === index ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`} data-id="element-7318">
            {entry.label}
          </button>)}
      </div>

      <p className="text-center text-slate-500 text-sm mt-4" data-id="element-7319">
        Klik op een punt of gebruik de pijltjestoetsen
      </p>
    </div>;
}